import React from "react";
import "./CompetitionKnockoutDraw.css";

import Select from "react-select";

import moment from "moment";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import useAxios from "../../useAxios";

const CompetitionKnockoutDraw = ({ competition, readonly = false, logout }) => {
  const [knockout, setKnockout] = React.useState(null);
  const [tables, setTables] = React.useState(null);
  const [disableDraw, setDisableDraw] = React.useState(false);
  const [axios] = useAxios(true, logout);

  React.useEffect(() => {
    const fetchData = async () => {
      await getKnockout();
      await axios.get("tables", setTables);
    };
    fetchData();
  }, []);

  const getKnockout = async () => {
    if (readonly) {
      await axios.get(
        "public/competition/knockout/" + competition.id,
        setKnockout
      );
    } else {
      await axios.get("competitionknockout/" + competition.id, setKnockout);
    }
  };

  const handleTableChange = (table, match) => {
    match.table = table;
    axios.post("match/" + match.id, match, getKnockout);
  };

  const handleDateTimeChange = (dateTime, match) => {
    // Update the state immediately for UI responsiveness
    setKnockout((prevKnockout) => {
      const newKnockout = { ...prevKnockout };
      newKnockout.rounds = newKnockout.rounds.map((round) => ({
        ...round,
        matches: round.matches.map((m) =>
          m.match && m.match.id === match.id
            ? { ...m, match: { ...m.match, dateTime: dateTime } }
            : m
        ),
      }));
      return newKnockout;
    });

    // Send to backend - convert date to ISO string to avoid timezone issues
    const matchUpdate = {
      id: match.id,
      name: match.name,
      dateTime: dateTime
        ? new Date(
            dateTime.getTime() - dateTime.getTimezoneOffset() * 60000
          ).toISOString()
        : null,
      raceTo: match.raceTo,
      home: {
        id: match.home.id,
        score: match.home.score,
        dishes: match.home.dishes,
        winner: match.home.winner,
        player: {
          id: match.home.player.id,
          name: match.home.player.name,
          handicap: match.home.player.handicap,
        },
      },
      away: {
        id: match.away.id,
        score: match.away.score,
        dishes: match.away.dishes,
        winner: match.away.winner,
        player: {
          id: match.away.player.id,
          name: match.away.player.name,
          handicap: match.away.player.handicap,
        },
      },
      table: match.table,
      isStreamed: match.isStreamed,
    };
    axios.post("match/" + match.id, matchUpdate, getKnockout);
  };

  const handleEstimatedDateTimeChange = (dateTime, roundMatch) => {
    // Update the state immediately for UI responsiveness
    setKnockout((prevKnockout) => {
      const newKnockout = { ...prevKnockout };
      newKnockout.rounds = newKnockout.rounds.map((round) => ({
        ...round,
        matches: round.matches.map((match) =>
          match.id === roundMatch.id
            ? { ...match, estimatedDateTime: dateTime }
            : match
        ),
      }));
      return newKnockout;
    });

    // Send to backend
    axios.post(
      "competitionknockout/round-match/" +
        roundMatch.id +
        "/estimated-datetime",
      { estimatedDateTime: dateTime },
      getKnockout
    );
  };

  const handleEstimatedTableChange = (table, roundMatch) => {
    // Update the state immediately for UI responsiveness
    setKnockout((prevKnockout) => {
      const newKnockout = { ...prevKnockout };
      newKnockout.rounds = newKnockout.rounds.map((round) => ({
        ...round,
        matches: round.matches.map((match) =>
          match.id === roundMatch.id ? { ...match, table: table } : match
        ),
      }));
      return newKnockout;
    });

    // Send to backend
    axios.post(
      "competitionknockout/round-match/" + roundMatch.id + "/table",
      { table: table },
      getKnockout
    );
  };

  const isRoundComplete = (round) => {
    return round.matches.every(
      (match) => match.match === null || match.match.status === 2
    );
  };

  const toggleRound = (roundId) => {
    const element = document.getElementById(`round-${roundId}`);
    const header = document.getElementById(`round-header-${roundId}`);

    if (element.style.display === "none") {
      element.style.display = "block";
      header.classList.add("open");
    } else {
      element.style.display = "none";
      header.classList.remove("open");
    }
  };

  const handleDraw = () => {
    setDisableDraw(true);
    axios.post("competitionknockout/" + competition.id + "/draw", {}, () =>
      window.location.reload()
    );
  };

  const handleStart = () => {
    axios.post("competition/" + competition.id + "/start", {}, () =>
      window.location.reload()
    );
  };

  const handleEnd = () => {
    axios.post("competition/" + competition.id + "/end", {}, () =>
      window.location.reload()
    );
  };

  const handleGeneratePin = (match) => {
    axios.post("match/" + match.id + "/generate-pin", {}, (response) => {
      const formattedDateTime = moment(match.dateTime).format(
        "DD MMM yyyy HH:mm"
      );
      const siteUrl = window.location.origin;
      const matchLoginUrl = `${siteUrl}/login/match/${match.id}`;

      const shareMessage = `Your match is booked for ${formattedDateTime} on ${match.table.name}. Please use the scoreboard here ${matchLoginUrl} with PIN ${response.pin}.`;

      // Use native Web Share API if available (mobile devices)
      if (navigator.share) {
        navigator
          .share({
            title: "Match Details",
            text: shareMessage,
          })
          .catch((error) => {
            // Fallback to clipboard if share fails
            fallbackShare(shareMessage);
          });
      } else {
        // Fallback for desktop browsers
        fallbackShare(shareMessage);
      }

      getKnockout(); // Refresh to show the PIN
    });
  };

  const fallbackShare = (message) => {
    // Try to copy to clipboard
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(message)
        .then(() => {
          alert("Match details copied to clipboard!");
        })
        .catch(() => {
          // Final fallback - show message in alert
          alert(`Share this message:\n\n${message}`);
        });
    } else {
      // Final fallback - show message in alert
      alert(`Share this message:\n\n${message}`);
    }
  };

  const canGeneratePin = (match) => {
    return match && match.dateTime && match.table && match.status !== 2;
  };

  // Helper function to get handicap for a specific game type
  const getHandicapForGameType = (player, gameTypeId) => {
    if (!player || !gameTypeId) {
      return 0;
    }
    const handicapEntry = (player.handicaps || []).find(h => h.gameTypeId === gameTypeId);
    return handicapEntry?.value ?? 0;
  };

  const getMatchDescription = (gameNumber, side) => {
    let game = null;

    for (let i = 0; i < knockout.rounds.length && game === null; i++) {
      const g = knockout.rounds[i].matches.filter(
        (x) => x.gameNumber === gameNumber
      );
      if (g.length > 0) {
        game = g[0];
      }
    }

    if (game !== null) {
      const match = game[side]?.match;
      if (match !== null && match.status === 2) {
        return match.home.winner
          ? match.home.player.name
          : match.away.player.name;
      }

      return "Winner of Match " + game[side]?.gameNumber;
    }

    return "something went wrong";
  };

  if (knockout === null) {
    return <div>Loading...</div>;
  }

  // Filter tables by competition game type
  const availableTables = (tables || []).filter((t) => {
    // If competition has a game type, only show tables with matching game type
    if (knockout?.competition?.gameType?.id) {
      return t.gameType?.id === knockout.competition.gameType.id;
    }
    return true;
  });

  return (
    <div>
      {!readonly && competition.status === 0 && (
        <button onClick={handleDraw} disabled={disableDraw}>
          {knockout.rounds.length > 0 ? "Re-" : ""}Draw
        </button>
      )}
      {!readonly && competition.status === 0 && (
        <button onClick={handleStart}>Start</button>
      )}
      {!readonly && competition.status === 1 && (
        <button onClick={handleEnd}>End</button>
      )}
      {knockout.rounds.map((round, roundNumber) => {
        const roundComplete = isRoundComplete(round);
        return (
          <div key={round.id} className="round-accordion">
            <h2
              id={`round-header-${round.id}`}
              className={`round-header ${!roundComplete ? "open" : ""}`}
              onClick={() => toggleRound(round.id)}
            >
              <span className="accordion-icon">
                {!roundComplete ? "▼" : "▶"}
              </span>
              {round.name} - Race to {knockout.raceTo}
            </h2>
            <div
              id={`round-${round.id}`}
              className="round-content"
              style={{ display: !roundComplete ? "block" : "none" }}
            >
              <table>
                <thead>
                  <tr>
                    <th>Game</th>
                    <th>Time</th>
                    <th>Home</th>
                    <th>Score</th>
                    <th></th>
                    <th>Score</th>
                    <th>Away</th>
                    <th>Table</th>
                    <th>Pin</th>
                  </tr>
                </thead>
                <tbody>
                  {round.matches.map((m) => {
                    if (m.match === null) {
                      return (
                        <tr key={m.id}>
                          <td>{m.gameNumber}</td>
                          <td>
                            {!readonly &&
                            competition.status === 1 &&
                            !competition.sameDay ? (
                              <DatePicker
                                selected={
                                  m.estimatedDateTime
                                    ? new Date(m.estimatedDateTime)
                                    : null
                                }
                                onChange={(dateTime) =>
                                  handleEstimatedDateTimeChange(dateTime, m)
                                }
                                showTimeSelect
                                timeFormat="HH:mm"
                                timeIntervals={15}
                                timeCaption="Time"
                                dateFormat="dd MMM yyyy HH:mm"
                                className="form-control"
                                placeholderText="Set time"
                              />
                            ) : m.estimatedDateTime === null ? (
                              ""
                            ) : (
                              moment(m.estimatedDateTime).format(
                                "DD MMM yyyy HH:mm"
                              )
                            )}
                          </td>
                          <td>{getMatchDescription(m.gameNumber, "home")}</td>
                          <td></td>
                          <td>v</td>
                          <td></td>
                          <td>{getMatchDescription(m.gameNumber, "away")}</td>
                          <td>
                            {!readonly && competition.status === 1 ? (
                              <Select
                                value={m.table}
                                onChange={(option) =>
                                  handleEstimatedTableChange(option, m)
                                }
                                options={availableTables}
                                isClearable={true}
                                placeholder="Select..."
                                getOptionValue={(option) => option.id}
                                getOptionLabel={(option) => option.name}
                                isSearchable={false}
                              />
                            ) : m.table ? (
                              m.table.name
                            ) : (
                              ""
                            )}
                          </td>
                          <td></td>
                        </tr>
                      );
                    }

                    return (
                      <tr
                        key={m.id}
                        className={m.match.status === 2 ? "finished" : ""}
                      >
                        <td>{m.gameNumber}</td>
                        <td
                          className={
                            m.match.status === 2 && !m.match.dateTime
                              ? "hide-on-mobile"
                              : ""
                          }
                        >
                          {!readonly &&
                          competition.status === 1 &&
                          !competition.sameDay &&
                          m.match.status === 0 ? (
                            <DatePicker
                              selected={
                                m.match.dateTime
                                  ? new Date(m.match.dateTime)
                                  : null
                              }
                              onChange={(dateTime) =>
                                handleDateTimeChange(dateTime, m.match)
                              }
                              showTimeSelect
                              timeFormat="HH:mm"
                              timeIntervals={15}
                              timeCaption="Time"
                              dateFormat="dd MMM yyyy HH:mm"
                              className="form-control"
                              placeholderText="Set time"
                            />
                          ) : m.match.dateTime === null ? (
                            ""
                          ) : (
                            moment(m.match.dateTime).format("DD MMM yyyy HH:mm")
                          )}
                        </td>
                        <td>
                          <span className="desktop-only">
                            {m.match.home.player === null
                              ? "Bye"
                              : `${m.match.home.player.name} (${getHandicapForGameType(m.match.home.player, knockout.competition?.gameType?.id)})`}
                          </span>
                          <span className="mobile-only">
                            {m.match.home.player === null
                              ? "Bye"
                              : `${m.match.home.player.name} [${m.match.home.score}]`}
                          </span>
                        </td>
                        <td>{m.match.home.score}</td>
                        <td>v</td>
                        <td>{m.match.away.score}</td>
                        <td>
                          <span className="desktop-only">
                            {m.match.away.player === null
                              ? "Bye"
                              : `(${getHandicapForGameType(m.match.away.player, knockout.competition?.gameType?.id)}) ${m.match.away.player.name}`}
                          </span>
                          <span className="mobile-only">
                            {m.match.away.player === null
                              ? "Bye"
                              : `${m.match.away.player.name} [${m.match.away.score}]`}
                          </span>
                        </td>
                        <td
                          className={
                            m.match.status === 2 && !m.match.table
                              ? "hide-on-mobile"
                              : ""
                          }
                        >
                          {!readonly && m.match.status !== 2 && (
                            <Select
                              value={m.match.table}
                              onChange={(option) =>
                                handleTableChange(option, m.match)
                              }
                              options={availableTables}
                              isClearable={true}
                              placeholder="Select..."
                              getOptionValue={(option) => option.id}
                              getOptionLabel={(option) => option.name}
                              isSearchable={false}
                            />
                          )}
                          {readonly || m.match.status === 2
                            ? m.match.table
                              ? m.match.table.name
                              : ""
                            : null}
                        </td>
                        <td>
                          {m.match.status !== 2 && m.match.pin && (
                            <span
                              style={{ fontWeight: "bold", fontSize: "1.1em" }}
                            >
                              {m.match.pin}
                            </span>
                          )}
                          {!readonly && canGeneratePin(m.match) && (
                            <button
                              onClick={() => handleGeneratePin(m.match)}
                              className="edit-button"
                              style={{ marginLeft: m.match.pin ? "10px" : "0" }}
                            >
                              Share
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CompetitionKnockoutDraw;
